import { MouseEvent, useEffect } from 'react';
import { breakpoints } from '../../constants';
import { RoutePaths } from '../../constants/routes';
import styles from './Sidebar.module.scss';
import { SidebarNavLink, SidebarNavLinkProps } from './SidebarNavLink/SidebarNavLink';
import { Icon, RolePermissionChecker, Text } from 'components';
import { useDevice } from 'hooks';
import { appActions } from 'store/app/slice';
import { selectPatientCasesCount } from 'store/patients/selectors';
import { patientsActions } from 'store/patients/slice';
import { useDispatch, useSelector } from 'store/utils';
import { UserRole } from 'types';

const sidebarNavItems: (Omit<SidebarNavLinkProps, 'closeMobileMenu'> & {
  rolesAllowed: UserRole[];
})[] = [
  {
    label: 'Actions required',
    icon: 'Home',
    linkTo: RoutePaths.DASHBOARD,
    rolesAllowed: [UserRole.ADMIN, UserRole.CLINIC, UserRole.DOCTOR, UserRole.READ_ONLY],
  },
  {
    label: 'All Patients',
    icon: 'Patients',
    linkTo: RoutePaths.PATIENTS,
    rolesAllowed: [UserRole.ADMIN, UserRole.CLINIC, UserRole.DOCTOR, UserRole.READ_ONLY],
  },
  {
    label: 'User Console',
    icon: 'UserControl',
    linkTo: RoutePaths.BASE_PATH,
    rolesAllowed: [UserRole.ADMIN],
  },
];

type Props = {
  isMobileMenuOpen: boolean;
  closeMobileMenu(): void;
};

export const Sidebar = ({ isMobileMenuOpen, closeMobileMenu }: Props) => {
  const { isPhone } = useDevice();

  const dispatch = useDispatch();
  const count = useSelector(selectPatientCasesCount);

  useEffect(() => {
    dispatch(patientsActions.getPatientCaseCount());

    const updateData = () => {
      if (document.visibilityState === 'visible') {
        dispatch(patientsActions.getPatientCaseCount());
      }
    };

    window.addEventListener('visibilitychange', updateData);

    return () => window.removeEventListener('visibilitychange', updateData);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isMobileMenuOpen]);

  const onSidebarClickHandler = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const onLogoutClickHandler = () => {
    closeMobileMenu();
    dispatch(appActions.setIsModalOpened({ name: 'logout', isOpened: true }));
  };

  if (!isMobileMenuOpen && isPhone) return null;

  return (
    <div className={styles.backdrop} onClick={closeMobileMenu}>
      <div className={styles.sidebarContainer} onClick={onSidebarClickHandler}>
        <div className={styles.sidebarHeader}>
          <Icon name="CircleLogo" className={styles.logo} />
          <div className={styles.navigationContainer}>
            {sidebarNavItems.map((item) => (
              <RolePermissionChecker rolesAllowedToView={item.rolesAllowed} key={item.label}>
                <SidebarNavLink
                  label={item.label}
                  icon={item.icon}
                  linkTo={item.linkTo}
                  closeMobileMenu={closeMobileMenu}
                  count={item.label === 'All Patients' ? count : null}
                />
              </RolePermissionChecker>
            ))}
          </div>
        </div>

        <div className={styles.sidebarFooter}>
          <button className={styles.logoutButton} onClick={onLogoutClickHandler}>
            <Icon name="LogOut" className={styles.logoutIcon} />
            <Text type="body2">Log Out</Text>
          </button>
        </div>
      </div>
    </div>
  );
};
